import FormField from 'components/FormField';
import { useHelpContext } from '../HelpContext';
import { FV } from './share';
import { Button, Form, Modal, ModalProps, TimePicker } from 'antd';
import { FormOutlined, PlusOutlined } from '@ant-design/icons';
import useModal from 'hooks/useModal';
import { Formik } from 'formik';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { DisabledProps } from 'views/Aide/Scenario/TaskForm/share';
import RangeBlur from 'components/RangeBlur';
import { useTranslate } from '@tolgee/react';

function TimeRangeScopes({ disabled }: DisabledProps) {
  const { t } = useTranslate();

  const {
    stepState: [, setHelpStep],
  } = useHelpContext();

  const { visible, show, hide, meta } = useModal<FV['timeRangeScopes']>();

  return (
    <FormField<FV['timeRangeScopes']>
      name="timeRangeScopes"
      required
      label={t('直播时间段')}
      validate={(scopes) => (scopes?.length ? undefined : t('请添加时间段'))}
    >
      {({ field: { value, onChange, onBlur } }) => {
        const onEdit = () => {
          setHelpStep('活动生效期');
          show(value);
        };

        const modal = (
          <TimeRangeScopesModal
            visible={visible}
            timeRangeScopes={meta}
            onCancel={() => {
              hide();
              onBlur();
            }}
            onSave={(value) => {
              onChange(value);
              hide();
              onBlur();
            }}
          />
        );

        if (!value?.length) {
          return [
            <Button
              className="!pl-0"
              disabled={disabled}
              type="link"
              icon={<PlusOutlined />}
              onClick={onEdit}
            >
              {t('添加时间段')}
            </Button>,
            modal,
          ];
        }

        return (
          <div>
            {value.map(({ startAt, endAt }) => (
              <span className="inline-block mr-3">{`${startAt}-${endAt}`}</span>
            ))}
            <Button disabled={disabled} type="text" icon={<FormOutlined />} onClick={onEdit} />
            {modal}
          </div>
        );
      }}
    </FormField>
  );
}

function TimeRangeScopesModal({
  timeRangeScopes,
  onSave,
  ...otherProps
}: ModalProps & {
  timeRangeScopes: FV['timeRangeScopes'];
  onSave: (scopes: FV['timeRangeScopes']) => void;
}) {
  const { t } = useTranslate();

  const initialValues = useMemo(() => {
    return {
      timeRangeScopes: timeRangeScopes || [
        {
          startAt: '',
          endAt: '',
        },
        {
          startAt: '',
          endAt: '',
        },
      ],
    };
  }, [timeRangeScopes]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={({ timeRangeScopes }) =>
        onSave(timeRangeScopes.filter(({ startAt, endAt }) => startAt && endAt))
      }
    >
      {({ handleSubmit }) => {
        return (
          <Modal
            width={378}
            title={t('直播时间段')}
            okText={t('保存')}
            onOk={() => handleSubmit()}
            {...otherProps}
          >
            <Form layout="vertical">
              <FormField<(typeof initialValues)['timeRangeScopes']>
                className="!mb-0"
                name="timeRangeScopes"
                required
                label={t('时间段')}
              >
                {({ field: { value: list, onChange: onListChange } }) => {
                  return [
                    ...list.map((_, index) => {
                      return (
                        <FormField<(typeof initialValues)['timeRangeScopes'][0]>
                          name={`timeRangeScopes.${index}`}
                          validate={({ startAt, endAt }) => {
                            if (
                              (!startAt || !endAt) &&
                              list.every(({ startAt, endAt }) => !startAt || !endAt)
                            ) {
                              return t('multiTimeSelect.请填写时间');
                            }
                            const pre = list.findLast(
                              ({ startAt, endAt }, i) => i < index && startAt && endAt,
                            );
                            const { endAt: preEnd } = pre || {};
                            if (
                              preEnd &&
                              dayjs(startAt, 'HH:mm').isBefore(dayjs(preEnd, 'HH:mm'))
                            ) {
                              return t('multiTimeSelect.earlierTip');
                            }
                          }}
                        >
                          {({
                            field: {
                              value: { startAt, endAt },
                              onChange,
                              onBlur,
                            },
                          }) => {
                            return (
                              <div>
                                <RangeBlur
                                  Component={TimePicker.RangePicker}
                                  allowClear={false}
                                  showSecond={false}
                                  format="HH:mm"
                                  value={
                                    startAt && endAt
                                      ? ([dayjs(startAt, 'HH:mm'), dayjs(endAt, 'HH:mm')] as any)
                                      : undefined
                                  }
                                  onChange={(range) => {
                                    const [start, end] = range || [];
                                    if (start && end) {
                                      onChange({
                                        startAt: start.format('HH:mm'),
                                        endAt: end.format('HH:mm'),
                                      });
                                    }
                                  }}
                                  onBlur={onBlur}
                                />
                                {index > 0 && (
                                  <Button
                                    type="link"
                                    onClick={() =>
                                      onListChange([
                                        ...list.slice(0, index),
                                        ...list.slice(index + 1),
                                      ])
                                    }
                                  >
                                    {t('phrase.删除')}
                                  </Button>
                                )}
                              </div>
                            );
                          }}
                        </FormField>
                      );
                    }),
                    list.length < 4 && (
                      <Button
                        className="!pl-0"
                        type="link"
                        icon={<PlusOutlined />}
                        onClick={() =>
                          onListChange([
                            ...list,
                            {
                              startAt: '',
                              endAt: '',
                            },
                          ])
                        }
                      >
                        {t('marketing.新增')}
                      </Button>
                    ),
                  ];
                }}
              </FormField>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
}

export default TimeRangeScopes;
